import React, { useCallback, useMemo } from "react";

import { Link, Switch, NavLink, Redirect, useLocation } from "react-router-dom";

import { useTranslation } from "react-i18next";

import qs from "query-string";
import clsx from "clsx";
import dayjs from "dayjs";

import Route from "../../components/Route";

import { useCurrentHasPlan } from "../../components/PlanBlock";

import AccessEventsRoute from "./AccessEvents";
import ActivityDashboard from "./Dashboard";
import CheckedIn from "./CheckedIn";
import PreRegistrationsRoute from "./PreRegistrations";
import useHasRole from "../../lib/hooks/useHasRole";
import { useCurrentClient } from "../../context/Client";

const ActivityRoute = React.memo(() => {
  const location = useLocation();

  const { t } = useTranslation(["activity", "preRegistrations"]);

  const { pathname } = useLocation();

  const query = useMemo(
    () =>
      qs.parse(location.search, { parseNumbers: true }) as {
        startDate?: Date;
        endDate?: Date;
      },
    [location.search],
  );

  const isFree = useCurrentHasPlan(["free"]);

  const isActivityCheckedIn = useHasRole(["ACTIVITY_CHECKED_IN"]);

  const currentClient = useCurrentClient();
  const preRegistrationEnabled =
    currentClient?.preferences?.preRegistration?.enabled === true;

  const renderBreadCrumb = useCallback(() => {
    if (pathname.endsWith("activity")) {
      return (
        <ol className="breadcrumb my-3">
          <li className="breadcrumb-item">{t("activity:nav.activity")}</li>
        </ol>
      );
    }

    if (pathname.endsWith("accessEvents")) {
      return (
        <ol className="breadcrumb my-3">
          <li className="breadcrumb-item">
            <Link to="/activity">{t("activity:nav.activity")}</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {t("activity:nav.accessEvent", { count: 2 })}
          </li>
        </ol>
      );
    }

    if (pathname.endsWith("export")) {
      return (
        <ol className="breadcrumb my-3">
          <li className="breadcrumb-item">
            <Link to="/activity">{t("activity:nav.activity")}</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/activity/accessEvents">
              {t("activity:nav.accessEvent", { count: 2 })}
            </Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {t("activity:nav.export")}
          </li>
        </ol>
      );
    }

    if (pathname.endsWith("checkedIn")) {
      return (
        <ol className="breadcrumb my-3">
          <li className="breadcrumb-item">
            <Link to="/activity">{t("activity:nav.activity")}</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {t("activity:nav.checkedIn")}
          </li>
        </ol>
      );
    }

    if (pathname.includes("preRegistrations")) {
      return (
        <ol className="breadcrumb my-3">
          <li className="breadcrumb-item">
            <Link to="/activity">{t("activity:nav.activity")}</Link>
          </li>
          {pathname.endsWith("preRegistrations") ? (
            <li className="breadcrumb-item active" aria-current="page">
              {t("preRegistrations:nav.list")}
            </li>
          ) : (
            <>
              <li className="breadcrumb-item">
                <Link to="/activity/preRegistrations">
                  {t("preRegistrations:nav.list")}
                </Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                {t("preRegistrations:nav.detail")}
              </li>
            </>
          )}
        </ol>
      );
    }

    return (
      <ol className="breadcrumb my-3">
        <li className="breadcrumb-item">
          <Link to="/activity">{t("activity:nav.activity")}</Link>
        </li>
        <li className="breadcrumb-item">
          <Link to="/activity/accessEvents">
            {t("activity:nav.accessEvent", { count: 2 })}
          </Link>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          {t("activity:nav.accessEvent", { count: 1 })}
        </li>
      </ol>
    );
  }, [pathname, t]);

  return (
    <div className="container-fluid">
      <nav aria-label="breadcrumb">{renderBreadCrumb()}</nav>
      {!isActivityCheckedIn && (
        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <NavLink
              exact
              className="nav-link"
              to={{
                pathname: "/activity",
                search: qs.stringify({
                  startDate:
                    query.startDate ||
                    dayjs().startOf("month").format("YYYY-MM-DD"),
                  endDate: query.endDate || dayjs().format("YYYY-MM-DD"),
                }),
              }}
            >
              {t("activity:tab.dashboard")}
            </NavLink>
          </li>
          <li className="nav-item">
            <NavLink
              className={clsx("nav-link", {
                disabled: isFree,
              })}
              to={{
                pathname: "/activity/accessEvents",
                search: qs.stringify({
                  startDate:
                    query.startDate ||
                    dayjs().startOf("month").format("YYYY-MM-DD"),
                  endDate: query.endDate || dayjs().format("YYYY-MM-DD"),
                  page: 1,
                }),
              }}
            >
              {t("activity:tab.accessEvents")}
            </NavLink>
          </li>
          <li className="nav-item">
            <NavLink
              exact
              className={clsx("nav-link", {
                disabled: isFree,
              })}
              to="/activity/checkedIn"
            >
              {t("activity:tab.checkedIn")}
            </NavLink>
          </li>
          {preRegistrationEnabled && (
            <li className="nav-item">
              <NavLink
                className={clsx("nav-link", {
                  disabled: isFree,
                })}
                to={{
                  pathname: "/activity/preRegistrations",
                  search: qs.stringify({
                    from: dayjs().format("YYYY-MM-DD"),
                    to: dayjs().add(7, "day").format("YYYY-MM-DD"),
                    page: 1,
                  }),
                }}
              >
                {t("preRegistrations:tab.preRegistrations")}
              </NavLink>
            </li>
          )}
        </ul>
      )}

      <div className="tab-content">
        <Switch>
          <Route.Private exact path="/activity">
            {isActivityCheckedIn ? (
              <Redirect to="/activity/checkedIn" />
            ) : (
              <div className="tab-pane active" role="tabpanel">
                <ActivityDashboard />
              </div>
            )}
          </Route.Private>
          {!isFree && (
            <>
              <Route.Private path="/activity/accessEvents">
                <div className="tab-pane active" role="tabpanel">
                  <AccessEventsRoute />
                </div>
              </Route.Private>
              <Route.Private exact path="/activity/checkedIn">
                <div className="tab-pane active" role="tabpanel">
                  <CheckedIn />
                </div>
              </Route.Private>
              {preRegistrationEnabled && (
                <Route.Private path="/activity/preRegistrations">
                  <div className="tab-pane active" role="tabpanel">
                    <PreRegistrationsRoute />
                  </div>
                </Route.Private>
              )}
            </>
          )}
          <Redirect to="/activity" />
        </Switch>
      </div>
    </div>
  );
});

export default ActivityRoute;
